import { Banner } from '@components';
import { Box, Button, Typography, useTheme } from '@mui/material';
import { BoxAdd } from 'iconsax-react';
import type { FC } from 'react';

import {
  LOCATION_HAS_NO_PRODUCTS_MESSAGE,
  LOCATION_HAS_NO_PRODUCTS_TITLE,
} from '@core/formik/quote';
import { useFirstProductRow } from '@core/hooks/useFirstProductRow';
import { hasValue } from '@core/utils/equality';
import { useLocationProductsLeft } from '@web/hooks/useLocationProductsLeft';

type Props = {
  plantId: string;
  /** Products already in a row, which can't be added again; empty where a product may repeat. */
  productIds: Array<string | null>;
  productsPath: string;
  onAdd: () => void;
};

/** Under a location's product rows: Add Product, or a warning when the location has no products. */
export const LocationProductsFooter: FC<Props> = ({ plantId, productIds, productsPath, onAdd }) => {
  const theme = useTheme();

  const excludedProductIds = productIds.filter(hasValue);
  const { count: productsLeft, isLoading } = useLocationProductsLeft(plantId, excludedProductIds);
  useFirstProductRow({ productsPath, plantId, productsLeft, onAdd });

  // Each empty row will take one of the products left.
  const emptyRowCount = productIds.length - excludedProductIds.length;
  const isNoneLeft = hasValue(productsLeft) && productsLeft <= emptyRowCount;
  const isLocationEmpty = productsLeft === 0 && excludedProductIds.length === 0;
  const isDisabled = isLoading || isNoneLeft;

  if (isLocationEmpty) {
    return (
      <Banner severity='warning' title={LOCATION_HAS_NO_PRODUCTS_TITLE}>
        {LOCATION_HAS_NO_PRODUCTS_MESSAGE}
      </Banner>
    );
  }

  return (
    <Box display='flex' alignItems='center' gap={1}>
      <Button
        disabled={isDisabled}
        onClick={onAdd}
        sx={{
          textTransform: 'none',
          color: theme.palette.SlabIndigo['700'],
          justifyContent: 'flex-start',
        }}
        startIcon={
          <BoxAdd
            color={isDisabled ? theme.palette.action.disabled : theme.palette.SlabIndigo['700']}
          />
        }
      >
        Add Product
      </Button>
      {isNoneLeft && (
        <Typography variant='body2' sx={{ color: theme.palette.text.secondary }}>
          No products left to add at this location
        </Typography>
      )}
    </Box>
  );
};
